import { XIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { DataTableFilterPicker } from './data-table-filter-picker';
import { filterSummary, type FilterSummary } from './filter-summary';
import type { FilterDef, FilterValue } from './types';
import { useResolvedOptions } from './use-resolved-options';

export interface DataTableFilterChipProps {
  def: FilterDef<never>;
  /** Undefined while the filter is being added and has no value yet. */
  value: FilterValue | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (next: FilterValue | undefined) => void;
  onRemove: () => void;
  /** Left / Right on the chip: the neighbouring chip, or the search box. */
  onNavigate?: (direction: -1 | 1) => void;
  /**
   * Where focus goes when the picker closes: an element, `false` to leave it
   * where it is, or `true` (the default) for the chip.
   */
  finalFocus?: () => HTMLElement | boolean | null;
  ref?: React.Ref<HTMLButtonElement>;
}

/** A chip's summary; `null` while the filter is being added and has no value. */
function useFilterChipSummary(def: FilterDef<never>, value: FilterValue | undefined) {
  const values = value && (value.op === 'in' || value.op === 'not_in') ? value.values : [];
  const resolved = useResolvedOptions(def, values);
  const { t, i18n } = useTranslation('common');
  return value ? filterSummary(def, value, { t, resolved, locale: i18n.language }) : null;
}

/** "Status is" and the tinted "Active, Draft": the text of every chip. */
function FilterChipLabel({
  def,
  summary,
}: {
  def: FilterDef<never>;
  summary: FilterSummary | null;
}) {
  const { t } = useTranslation('common');
  // A filter being added reads "Status is" until a value is picked.
  const pendingVerb =
    def.type === 'enum' || def.type === 'async' || def.type === 'boolean'
      ? ` ${t('indexTable.summary.verb.is')}`
      : '';
  return (
    <>
      <span className="text-foreground shrink-0">
        {def.label}
        {summary ? ` ${summary.verb}` : pendingVerb}
      </span>
      {summary ? (
        <span className="bg-primary/10 text-foreground min-w-0 truncate rounded-sm px-1 font-medium">
          {summary.detail}
        </span>
      ) : null}
    </>
  );
}

const CHIP = 'inline-flex h-7 max-w-full min-w-0 items-center rounded-md bg-muted text-sm';
const CHIP_FACE = 'flex h-full min-w-0 items-center gap-1 rounded-md px-2';

/**
 * An applied filter inside the search bar, as in the Shopify admin:
 * "Status is Active, Draft" with the values tinted. Clicking it reopens the
 * picker with the current values checked; Delete or Backspace removes it;
 * the x shows on hover and focus.
 */
function DataTableFilterChip({
  def,
  value,
  open,
  onOpenChange,
  onChange,
  onRemove,
  onNavigate,
  finalFocus,
  ref,
}: DataTableFilterChipProps) {
  const { t } = useTranslation('common');
  const summary = useFilterChipSummary(def, value);

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      onRemove();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      onNavigate?.(event.key === 'ArrowLeft' ? -1 : 1);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <span
        data-slot="data-table-filter-chip"
        data-filter={def.key}
        data-state={open ? 'open' : 'closed'}
        className={cn(CHIP, 'group/chip data-[state=open]:ring-ring data-[state=open]:ring-2')}
      >
        <PopoverTrigger
          ref={ref}
          onKeyDown={onKeyDown}
          aria-keyshortcuts="Delete Backspace"
          aria-label={
            summary
              ? t('indexTable.filters.chipLabel', { summary: summary.text })
              : t('indexTable.filters.chooseValue', { label: def.label })
          }
          title={summary?.text}
          className={cn(CHIP_FACE, 'focus-visible:ring-ring outline-none focus-visible:ring-2')}
        >
          <FilterChipLabel def={def} summary={summary} />
        </PopoverTrigger>
        {summary ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={onRemove}
            aria-label={t('indexTable.filters.remove', { summary: summary.text })}
            className={cn(
              'text-muted-foreground hover:text-foreground -ml-1 flex h-full w-6 items-center justify-center rounded-r-md',
              // Fades in on hover or focus, as Shopify's chips do. It keeps its
              // room: a chip that grew on hover could wrap away from the pointer
              // and flicker. Always shown for touch, which has no hover.
              'opacity-0 group-focus-within/chip:opacity-100 group-hover/chip:opacity-100 pointer-coarse:opacity-100',
            )}
          >
            <XIcon className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </span>
      <PopoverContent
        data-filter-popup=""
        align="start"
        className="w-auto p-0"
        onCloseAutoFocus={(event) => {
          const target = finalFocus?.() ?? true;
          if (target === true) return;
          event.preventDefault();
          if (target instanceof HTMLElement) target.focus();
        }}
      >
        <DataTableFilterPicker
          def={def}
          value={value}
          onChange={onChange}
          onDone={() => onOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * An invisible, inert copy of a chip at its natural width. The filter bar
 * lays these out off-screen to learn whether the real chips fit on one line.
 */
function DataTableFilterChipGhost({
  def,
  value,
}: {
  def: FilterDef<never>;
  value: FilterValue | undefined;
}) {
  const summary = useFilterChipSummary(def, value);
  return (
    <span className={cn(CHIP, 'max-w-none shrink-0')}>
      <span className={CHIP_FACE}>
        <FilterChipLabel def={def} summary={summary} />
      </span>
      {/* The remove button: w-6 pulled in by -ml-1. */}
      {summary ? <span className="w-5" /> : null}
    </span>
  );
}

export { DataTableFilterChip, DataTableFilterChipGhost, FilterChipLabel, useFilterChipSummary };
